import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Users, Calendar, Mail, TrendingUp, LogOut, 
  Phone, CheckCircle, Loader2,
  Bell, MessageCircle, BarChart3, Target, ArrowUp,
  Filter, CalendarCheck, Send, FileText, Clock, Eye, Zap, RotateCcw, AlertTriangle
} from 'lucide-react';
import { getLeads, getAppointments, getStats, adminLogin, getNewsletterSubscribers, updateLead, updateAppointment } from '../lib/api';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

const Admin = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('analytics');
  const [stats, setStats] = useState({ total_leads: 0, new_leads: 0, total_appointments: 0, newsletter_subscribers: 0, unread_notifications: 0 });
  const [todayVisits, setTodayVisits] = useState(0);
  const [leads, setLeads] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [leadFilter, setLeadFilter] = useState('all'); // all, calendly, whatsapp, newsletter
  const [sendingReport, setSendingReport] = useState(false);
  const [reportMessage, setReportMessage] = useState(null);
  const [testingTelegram, setTestingTelegram] = useState(false);
  const [dropoffData, setDropoffData] = useState(null);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [resetting, setResetting] = useState(false);

  useEffect(() => {
    const auth = sessionStorage.getItem('admin_auth');
    if (auth === 'true') {
      setIsAuthenticated(true);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      loadData();
    }
  }, [isAuthenticated, activeTab]);

  const loadData = async () => {
    setLoading(true);
    try {
      const statsData = await getStats();
      setStats(statsData);
      
      // Always load notifications and leads for analytics
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const notifResponse = await fetch(`${API_URL}/api/admin/notifications`);
      const notifData = await notifResponse.json();
      setNotifications(notifData);

      // Load today's visits
      try {
        const visitsResponse = await fetch(`${API_URL}/api/analytics/visits/today`);
        const visitsData = await visitsResponse.json();
        setTodayVisits(visitsData.count || 0);
      } catch (err) {
        console.error('Error loading visits:', err);
      }

      // Load drop-off analysis
      try {
        const dropoffResponse = await fetch(`${API_URL}/api/admin/dropoff-analysis`);
        const dropoffResult = await dropoffResponse.json();
        setDropoffData(dropoffResult);
      } catch (err) {
        console.error('Error loading dropoff analysis:', err);
      }

      // Always load leads for analytics
      const leadsData = await getLeads();
      setLeads(leadsData);

      if (activeTab === 'appointments') {
        const apptsData = await getAppointments();
        setAppointments(apptsData);
      } else if (activeTab === 'newsletter') {
        const subsData = await getNewsletterSubscribers();
        setSubscribers(subsData);
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const markNotificationRead = async (notificationId) => {
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      await fetch(`${API_URL}/api/admin/notifications/${notificationId}`, {
        method: 'PATCH'
      });
      setNotifications(notifications.map(n => 
        n.id === notificationId ? { ...n, read: true } : n
      ));
      setStats(prev => ({ ...prev, unread_notifications: Math.max(0, prev.unread_notifications - 1) }));
    } catch (error) {
      console.error('Error marking notification read:', error);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    setLoginError('');
    try {
      await adminLogin(password);
      sessionStorage.setItem('admin_auth', 'true');
      setIsAuthenticated(true);
    } catch (error) {
      setLoginError('Contraseña incorrecta');
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('admin_auth');
    setIsAuthenticated(false);
    setPassword('');
  };

  const handleSendWeeklyReport = async () => {
    setSendingReport(true);
    setReportMessage(null);
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/admin/weekly-report`, {
        method: 'POST'
      });
      const data = await response.json();
      if (response.ok) {
        setReportMessage({ type: 'success', text: '✅ Reporte enviado exitosamente a tu email' });
      } else {
        setReportMessage({ type: 'error', text: `❌ Error: ${data.detail || 'No se pudo enviar'}` });
      }
    } catch (error) {
      setReportMessage({ type: 'error', text: '❌ Error de conexión al enviar el reporte' });
    } finally {
      setSendingReport(false);
      // Clear message after 5 seconds
      setTimeout(() => setReportMessage(null), 5000);
    }
  };

  const handleTestTelegram = async () => {
    setTestingTelegram(true);
    setReportMessage(null);
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/admin/test-telegram`, {
        method: 'POST'
      });
      const data = await response.json();
      if (response.ok) {
        setReportMessage({ type: 'success', text: '✅ Notificación de prueba enviada a Telegram' });
      } else {
        setReportMessage({ type: 'error', text: `❌ Error: ${data.detail || 'No se pudo enviar'}` });
      }
    } catch (error) {
      setReportMessage({ type: 'error', text: '❌ Error de conexión con Telegram' });
    } finally {
      setTestingTelegram(false);
      setTimeout(() => setReportMessage(null), 5000);
    }
  };

  const handleResetStats = async (resetAll = false) => {
    setResetting(true);
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const endpoint = resetAll 
        ? `${API_URL}/api/admin/reset-all?confirm=true&password=vidamujer2024`
        : `${API_URL}/api/admin/reset-stats?confirm=true`;
      
      const response = await fetch(endpoint, { method: 'POST' });
      const data = await response.json();
      
      if (response.ok) {
        setReportMessage({ type: 'success', text: `✅ ${data.message}` });
        loadData(); // Reload all data
      } else {
        setReportMessage({ type: 'error', text: `❌ Error: ${data.detail}` });
      }
    } catch (error) {
      setReportMessage({ type: 'error', text: '❌ Error al resetear' });
    } finally {
      setResetting(false);
      setShowResetConfirm(false);
      setTimeout(() => setReportMessage(null), 5000);
    }
  };

  const handleStatusChange = async (type, id, newStatus) => {
    try {
      if (type === 'lead') {
        await updateLead(id, { status: newStatus });
        setLeads(leads.map(l => l.id === id ? { ...l, status: newStatus } : l));
      } else {
        await updateAppointment(id, { status: newStatus });
        setAppointments(appointments.map(a => a.id === id ? { ...a, status: newStatus } : a));
      }
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  // Calculate analytics metrics
  const getAnalytics = () => {
    const totalLeads = leads.length;
    
    // Classify leads by their qualification tier based on notes or status
    const calendlyLeads = leads.filter(l => 
      l.status === 'qualified' || 
      l.status === 'appointment_scheduled' ||
      (l.notes && l.notes.includes('$7,000')) ||
      (l.recommended_suma && l.recommended_suma >= 100000)
    );
    
    const whatsappLeads = leads.filter(l => 
      l.status === 'qualified_pending' || 
      l.status === 'contacted' ||
      l.status === 'converted' ||
      (l.recommended_suma && l.recommended_suma >= 50000 && l.recommended_suma < 100000)
    );
    
    const newsletterLeads = leads.filter(l => l.status === 'newsletter');
    
    const convertedLeads = leads.filter(l => l.status === 'converted');
    
    // Calculate conversion rates
    const conversionRate = totalLeads > 0 ? ((convertedLeads.length / totalLeads) * 100).toFixed(1) : 0;
    const calendlyRate = calendlyLeads.length > 0 ? ((calendlyLeads.filter(l => l.status === 'converted').length / calendlyLeads.length) * 100).toFixed(1) : 0;
    const whatsappRate = whatsappLeads.length > 0 ? ((whatsappLeads.filter(l => l.status === 'converted').length / whatsappLeads.length) * 100).toFixed(1) : 0;
    
    return {
      totalLeads,
      calendlyLeads: calendlyLeads.length,
      whatsappLeads: whatsappLeads.length,
      newsletterLeads: newsletterLeads.length,
      convertedLeads: convertedLeads.length,
      conversionRate,
      calendlyRate,
      whatsappRate,
      pendingContact: notifications.filter(n => !n.read).length
    };
  };

  const analytics = getAnalytics();

  // Filter leads based on selected tier
  const getFilteredLeads = () => {
    switch (leadFilter) {
      case 'calendly':
        return leads.filter(l => 
          l.status === 'qualified' || 
          l.status === 'appointment_scheduled' ||
          (l.recommended_suma && l.recommended_suma >= 100000)
        );
      case 'whatsapp':
        return leads.filter(l => 
          l.status === 'qualified_pending' || 
          l.status === 'contacted' ||
          l.status === 'converted' ||
          (l.recommended_suma && l.recommended_suma >= 50000 && l.recommended_suma < 100000)
        );
      case 'newsletter':
        return leads.filter(l => l.status === 'newsletter');
      default:
        return leads;
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      new: 'bg-blue-100 text-blue-700',
      contacted: 'bg-yellow-100 text-yellow-700',
      appointment_scheduled: 'bg-purple-100 text-purple-700',
      qualified: 'bg-indigo-100 text-indigo-700',
      qualified_pending: 'bg-cyan-100 text-cyan-700',
      converted: 'bg-green-100 text-green-700',
      newsletter: 'bg-stone-100 text-stone-700',
      abandoned_cart: 'bg-orange-100 text-orange-700',
      scheduled: 'bg-blue-100 text-blue-700',
      completed: 'bg-green-100 text-green-700',
      cancelled: 'bg-red-100 text-red-700',
      no_show: 'bg-orange-100 text-orange-700'
    };
    return colors[status] || 'bg-stone-100 text-stone-700';
  };

  const getStatusLabel = (status) => {
    const labels = {
      new: 'Nuevo',
      contacted: 'Contactado',
      appointment_scheduled: 'Cita agendada',
      qualified: 'Calificado (Calendly)',
      qualified_pending: 'Calificado (WhatsApp)',
      converted: 'Convertido',
      newsletter: 'Newsletter',
      abandoned_cart: 'Carrito abandonado',
      scheduled: 'Agendada',
      completed: 'Completada',
      cancelled: 'Cancelada',
      no_show: 'No asistió'
    };
    return labels[status] || status;
  };

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md"
        >
          <div className="text-center mb-8">
            <img 
              src="https://customer-assets.emergentagent.com/job_womenlife-expert/artifacts/33njuh6q_Logo%20PL.png" 
              alt="Pablo López" 
              className="w-16 h-16 rounded-full object-cover mx-auto mb-4"
            />
            <h1 className="font-playfair text-2xl text-stone-900">Panel de Administración</h1>
            <p className="text-stone-500 text-sm mt-2">Pablo López - Vida Mujer</p>
          </div>

          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-2">Contraseña</label>
              <input
                data-testid="admin-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 rounded-xl border border-stone-200 focus:ring-2 focus:ring-[#0F2B5B]/20 focus:border-[#0F2B5B] transition-all"
                placeholder="Ingresa tu contraseña"
              />
            </div>
            {loginError && (
              <p className="text-red-500 text-sm">{loginError}</p>
            )}
            <button
              data-testid="admin-login-btn"
              type="submit"
              disabled={loginLoading}
              className="w-full py-3 rounded-xl bg-[#0F2B5B] text-white font-medium hover:bg-[#0F2B5B]/90 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loginLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Ingresar'}
            </button>
          </form>
        </motion.div>
      </div>
    );
  }

  // Dashboard
  return (
    <div className="min-h-screen bg-stone-50">
      {/* Header */}
      <header className="bg-white border-b border-stone-100 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img 
              src="https://customer-assets.emergentagent.com/job_womenlife-expert/artifacts/33njuh6q_Logo%20PL.png" 
              alt="Pablo López" 
              className="w-10 h-10 rounded-full object-cover"
            />
            <div>
              <span className="font-semibold text-stone-900">Panel de Administración</span>
              <span className="block text-xs text-stone-500">Vida Mujer - Analytics</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              data-testid="test-telegram-btn"
              onClick={handleTestTelegram}
              disabled={testingTelegram}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#0088cc] text-white hover:bg-[#0088cc]/90 transition-all disabled:opacity-50"
              title="Probar notificación de Telegram"
            >
              {testingTelegram ? (
                <Loader2 size={18} className="animate-spin" />
              ) : (
                <Zap size={18} />
              )}
              {testingTelegram ? 'Enviando...' : 'Test Telegram'}
            </button>
            <button
              data-testid="send-weekly-report-btn"
              onClick={handleSendWeeklyReport}
              disabled={sendingReport}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#0F2B5B] text-white hover:bg-[#0F2B5B]/90 transition-all disabled:opacity-50"
            >
              {sendingReport ? (
                <Loader2 size={18} className="animate-spin" />
              ) : (
                <FileText size={18} />
              )}
              {sendingReport ? 'Enviando...' : 'Enviar Reporte'}
            </button>
            <button
              data-testid="admin-logout-btn"
              onClick={handleLogout}
              className="flex items-center gap-2 px-4 py-2 rounded-lg hover:bg-stone-100 text-stone-600 transition-all"
            >
              <LogOut size={18} />
              Salir
            </button>
          </div>
        </div>
        {/* Report message */}
        {reportMessage && (
          <div className={`max-w-7xl mx-auto mt-2 px-4 py-2 rounded-lg text-sm ${
            reportMessage.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
          }`}>
            {reportMessage.text}
          </div>
        )}
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Refresh Button + Reset Button */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowResetConfirm(true)}
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm text-red-600 hover:bg-red-50 transition-all"
            >
              <RotateCcw size={16} />
              Resetear estadísticas
            </button>
          </div>
          <button
            onClick={loadData}
            disabled={loading}
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm text-stone-500 hover:bg-stone-100 transition-all disabled:opacity-50"
          >
            {loading ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <Clock size={16} />
            )}
            Actualizar datos
          </button>
        </div>

        {/* Reset Confirmation Modal */}
        {showResetConfirm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="bg-white rounded-2xl p-6 max-w-md mx-4 shadow-2xl"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                  <AlertTriangle className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg">¿Resetear estadísticas?</h3>
                  <p className="text-sm text-stone-500">Esta acción no se puede deshacer</p>
                </div>
              </div>
              
              <div className="space-y-3 mb-6">
                <button
                  onClick={() => handleResetStats(false)}
                  disabled={resetting}
                  className="w-full py-3 px-4 rounded-xl bg-orange-100 text-orange-700 hover:bg-orange-200 transition-all text-left"
                >
                  <span className="font-medium">Solo visitas</span>
                  <p className="text-xs mt-1">Resetea el contador de visitas únicas</p>
                </button>
                <button
                  onClick={() => handleResetStats(true)}
                  disabled={resetting}
                  className="w-full py-3 px-4 rounded-xl bg-red-100 text-red-700 hover:bg-red-200 transition-all text-left"
                >
                  <span className="font-medium">Todo (leads + visitas + conversaciones)</span>
                  <p className="text-xs mt-1">⚠️ Elimina TODOS los datos</p>
                </button>
              </div>
              
              <button
                onClick={() => setShowResetConfirm(false)}
                className="w-full py-2 text-stone-500 hover:text-stone-700"
              >
                Cancelar
              </button>
            </motion.div>
          </div>
        )}

        {/* Notifications Banner */}
        {notifications.filter(n => !n.read).length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 bg-gradient-to-r from-orange-500 to-red-500 rounded-2xl p-6 text-white shadow-lg"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                <Bell className="w-5 h-5 animate-pulse" />
              </div>
              <div>
                <h3 className="font-semibold text-lg">{notifications.filter(n => !n.read).length} Leads Esperando Contacto</h3>
                <p className="text-white/80 text-sm">Leads calificados que completaron el diagnóstico</p>
              </div>
            </div>
            
            <div className="space-y-3">
              {notifications.filter(n => !n.read).slice(0, 3).map(notif => (
                <div key={notif.id} className="bg-white/10 backdrop-blur rounded-xl p-4 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
                      <Phone className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="font-semibold">{notif.lead_name}</p>
                      <p className="text-white/80 text-sm flex items-center gap-2">
                        <Phone size={14} /> {notif.lead_phone}
                        {notif.profile_type && <span className="ml-2 px-2 py-0.5 bg-white/20 rounded-full text-xs">{notif.profile_type}</span>}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <a
                      href={notif.whatsapp_url || `https://wa.me/52${notif.lead_phone?.replace(/\D/g, '')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => markNotificationRead(notif.id)}
                      className="flex items-center gap-2 px-4 py-2 bg-[#25D366] hover:bg-[#20bd5a] rounded-lg font-medium transition-all"
                    >
                      <MessageCircle size={18} />
                      Contactar
                    </a>
                    <button
                      onClick={() => markNotificationRead(notif.id)}
                      className="p-2 hover:bg-white/20 rounded-lg transition-all"
                      title="Marcar como leído"
                    >
                      <CheckCircle size={18} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {[
            { id: 'analytics', label: 'Analytics', icon: BarChart3 },
            { id: 'leads', label: 'Leads', icon: Users },
            { id: 'appointments', label: 'Citas', icon: Calendar },
            { id: 'newsletter', label: 'Newsletter', icon: Mail }
          ].map(tab => (
            <button
              key={tab.id}
              data-testid={`tab-${tab.id}`}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all whitespace-nowrap ${
                activeTab === tab.id
                  ? 'bg-[#0F2B5B] text-white'
                  : 'bg-white text-stone-600 hover:bg-stone-100 border border-stone-200'
              }`}
            >
              <tab.icon size={18} />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-[#0F2B5B]" />
          </div>
        ) : (
          <>
            {/* Analytics Dashboard */}
            {activeTab === 'analytics' && (
              <div className="space-y-6">
                {/* Main Stats */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  {/* Today's Visits - NEW */}
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-gradient-to-br from-amber-500 to-orange-500 rounded-xl p-6 text-white shadow-lg"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center">
                        <Eye size={20} />
                      </div>
                      <span className="text-xs text-white/80">Hoy</span>
                    </div>
                    <p data-testid="today-visits-count" className="text-3xl font-bold">{todayVisits}</p>
                    <p className="text-sm text-white/80">Visitas únicas</p>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                    className="bg-white rounded-xl p-6 border border-stone-100 shadow-sm"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="w-10 h-10 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center">
                        <Users size={20} />
                      </div>
                      <span className="text-xs text-stone-500">Total</span>
                    </div>
                    <p className="text-3xl font-bold text-stone-900">{analytics.totalLeads}</p>
                    <p className="text-sm text-stone-500">Leads totales</p>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="bg-white rounded-xl p-6 border border-stone-100 shadow-sm"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="w-10 h-10 rounded-lg bg-green-50 text-green-600 flex items-center justify-center">
                        <CheckCircle size={20} />
                      </div>
                      <span className="flex items-center gap-1 text-xs text-green-600">
                        <ArrowUp size={12} /> {analytics.conversionRate}%
                      </span>
                    </div>
                    <p className="text-3xl font-bold text-stone-900">{analytics.convertedLeads}</p>
                    <p className="text-sm text-stone-500">Convertidos</p>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className="bg-white rounded-xl p-6 border border-stone-100 shadow-sm"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="w-10 h-10 rounded-lg bg-orange-50 text-orange-600 flex items-center justify-center">
                        <Bell size={20} />
                      </div>
                      {analytics.pendingContact > 0 && (
                        <span className="flex items-center gap-1 text-xs text-orange-600 animate-pulse">
                          Pendiente
                        </span>
                      )}
                    </div>
                    <p className="text-3xl font-bold text-stone-900">{analytics.pendingContact}</p>
                    <p className="text-sm text-stone-500">Por contactar</p>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                    className="bg-white rounded-xl p-6 border border-stone-100 shadow-sm"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="w-10 h-10 rounded-lg bg-purple-50 text-purple-600 flex items-center justify-center">
                        <Target size={20} />
                      </div>
                    </div>
                    <p className="text-3xl font-bold text-stone-900">{analytics.conversionRate}%</p>
                    <p className="text-sm text-stone-500">Tasa conversión</p>
                  </motion.div>
                </div>

                {/* Funnel by Tier */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* Calendly Tier */}
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                    className="bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl p-6 text-white"
                  >
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                        <CalendarCheck size={24} />
                      </div>
                      <div>
                        <h3 className="font-semibold">Calendly</h3>
                        <p className="text-white/70 text-xs">$7,000+ / Este mes</p>
                      </div>
                    </div>
                    <p className="text-4xl font-bold mb-1">{analytics.calendlyLeads}</p>
                    <p className="text-white/70 text-sm">leads de alto potencial</p>
                    <div className="mt-4 pt-4 border-t border-white/20">
                      <p className="text-sm flex items-center justify-between">
                        <span>Tasa conversión</span>
                        <span className="font-semibold">{analytics.calendlyRate}%</span>
                      </p>
                    </div>
                  </motion.div>

                  {/* WhatsApp Tier */}
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 }}
                    className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl p-6 text-white"
                  >
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                        <MessageCircle size={24} />
                      </div>
                      <div>
                        <h3 className="font-semibold">WhatsApp</h3>
                        <p className="text-white/70 text-xs">$3,000 - $6,999</p>
                      </div>
                    </div>
                    <p className="text-4xl font-bold mb-1">{analytics.whatsappLeads}</p>
                    <p className="text-white/70 text-sm">leads calificados</p>
                    <div className="mt-4 pt-4 border-t border-white/20">
                      <p className="text-sm flex items-center justify-between">
                        <span>Tasa conversión</span>
                        <span className="font-semibold">{analytics.whatsappRate}%</span>
                      </p>
                    </div>
                  </motion.div>

                  {/* Newsletter Tier */}
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.6 }}
                    className="bg-gradient-to-br from-stone-500 to-stone-600 rounded-xl p-6 text-white"
                  >
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                        <Mail size={24} />
                      </div>
                      <div>
                        <h3 className="font-semibold">Newsletter</h3>
                        <p className="text-white/70 text-xs">&lt; $3,000</p>
                      </div>
                    </div>
                    <p className="text-4xl font-bold mb-1">{analytics.newsletterLeads}</p>
                    <p className="text-white/70 text-sm">leads en nurturing</p>
                    <div className="mt-4 pt-4 border-t border-white/20">
                      <p className="text-sm flex items-center justify-between">
                        <span>Suscriptores</span>
                        <span className="font-semibold">{stats.newsletter_subscribers}</span>
                      </p>
                    </div>
                  </motion.div>
                </div>

                {/* Quick Stats Row */}
                <div className="bg-white rounded-xl border border-stone-100 shadow-sm p-6">
                  <h3 className="font-semibold text-stone-900 mb-4 flex items-center gap-2">
                    <TrendingUp size={20} />
                    Resumen Rápido
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    <div className="text-center p-4 bg-stone-50 rounded-lg">
                      <p className="text-2xl font-bold text-[#0F2B5B]">{stats.total_leads}</p>
                      <p className="text-xs text-stone-500">Total leads</p>
                    </div>
                    <div className="text-center p-4 bg-stone-50 rounded-lg">
                      <p className="text-2xl font-bold text-green-600">{stats.new_leads}</p>
                      <p className="text-xs text-stone-500">Nuevos</p>
                    </div>
                    <div className="text-center p-4 bg-stone-50 rounded-lg">
                      <p className="text-2xl font-bold text-purple-600">{stats.total_appointments}</p>
                      <p className="text-xs text-stone-500">Citas</p>
                    </div>
                    <div className="text-center p-4 bg-stone-50 rounded-lg">
                      <p className="text-2xl font-bold text-orange-600">{stats.newsletter_subscribers}</p>
                      <p className="text-xs text-stone-500">Newsletter</p>
                    </div>
                    <div className="text-center p-4 bg-stone-50 rounded-lg">
                      <p className="text-2xl font-bold text-red-600">{stats.unread_notifications || 0}</p>
                      <p className="text-xs text-stone-500">Sin contactar</p>
                    </div>
                  </div>
                </div>

                {/* Drop-off Analysis */}
                {dropoffData && (
                  <div className="bg-white rounded-xl border border-stone-100 shadow-sm p-6">
                    <h3 className="font-semibold text-stone-900 mb-4 flex items-center gap-2">
                      <BarChart3 size={20} />
                      Análisis de Abandono (Drop-off)
                    </h3>
                    <div className="mb-4 flex items-center gap-4 text-sm">
                      <span className="text-stone-600">Iniciaron: <strong>{dropoffData.total_started}</strong></span>
                      <span className="text-green-600">Completaron: <strong>{dropoffData.total_completed}</strong></span>
                      <span className="text-[#0F2B5B]">Tasa: <strong>{dropoffData.completion_rate}%</strong></span>
                    </div>
                    <div className="space-y-2">
                      {dropoffData.steps && dropoffData.steps.map((step, index) => (
                        <div key={step.step} className="flex items-center gap-3">
                          <div className="w-32 text-sm text-stone-600 truncate">{step.step_name}</div>
                          <div className="flex-1 bg-stone-100 rounded-full h-6 relative overflow-hidden">
                            <div 
                              className={`h-full rounded-full transition-all ${
                                step.step === 'completado' ? 'bg-green-500' : 
                                step.dropoff_rate > 30 ? 'bg-red-400' :
                                step.dropoff_rate > 15 ? 'bg-orange-400' : 'bg-blue-400'
                              }`}
                              style={{ width: `${Math.min(step.dropoff_rate * 2, 100)}%` }}
                            />
                            <span className="absolute inset-0 flex items-center justify-center text-xs font-medium text-stone-700">
                              {step.abandoned_here} abandonaron ({step.dropoff_rate}%)
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                    <p className="text-xs text-stone-400 mt-4">
                      💡 Las barras rojas indican etapas con alto abandono (&gt;30%). Considera simplificar esas preguntas.
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Leads Table */}
            {activeTab === 'leads' && (
              <div className="bg-white rounded-xl border border-stone-100 shadow-sm overflow-hidden">
                {/* Filter Buttons */}
                <div className="p-4 border-b border-stone-100 flex flex-wrap gap-2">
                  <span className="text-sm text-stone-500 flex items-center gap-2 mr-2">
                    <Filter size={16} /> Filtrar por:
                  </span>
                  {[
                    { id: 'all', label: 'Todos', count: leads.length },
                    { id: 'calendly', label: 'Calendly ($7k+)', count: analytics.calendlyLeads, color: 'bg-indigo-100 text-indigo-700' },
                    { id: 'whatsapp', label: 'WhatsApp ($3k-$7k)', count: analytics.whatsappLeads, color: 'bg-green-100 text-green-700' },
                    { id: 'newsletter', label: 'Newsletter (<$3k)', count: analytics.newsletterLeads, color: 'bg-stone-100 text-stone-700' }
                  ].map(filter => (
                    <button
                      key={filter.id}
                      onClick={() => setLeadFilter(filter.id)}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                        leadFilter === filter.id
                          ? 'bg-[#0F2B5B] text-white'
                          : filter.color || 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                      }`}
                    >
                      {filter.label} ({filter.count})
                    </button>
                  ))}
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-stone-50 border-b border-stone-100">
                      <tr>
                        <th className="text-left px-6 py-4 text-sm font-medium text-stone-600">Nombre</th>
                        <th className="text-left px-6 py-4 text-sm font-medium text-stone-600">Contacto</th>
                        <th className="text-left px-6 py-4 text-sm font-medium text-stone-600">Perfil</th>
                        <th className="text-left px-6 py-4 text-sm font-medium text-stone-600">Última Etapa</th>
                        <th className="text-left px-6 py-4 text-sm font-medium text-stone-600">Ingreso</th>
                        <th className="text-left px-6 py-4 text-sm font-medium text-stone-600">Interacción</th>
                        <th className="text-left px-6 py-4 text-sm font-medium text-stone-600">Estado</th>
                        <th className="text-left px-6 py-4 text-sm font-medium text-stone-600">Fecha</th>
                        <th className="text-left px-6 py-4 text-sm font-medium text-stone-600">Acción</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-stone-100">
                      {getFilteredLeads().length === 0 ? (
                        <tr>
                          <td colSpan={9} className="text-center py-12 text-stone-500">
                            No hay leads en esta categoría.
                          </td>
                        </tr>
                      ) : (
                        getFilteredLeads().map(lead => {
                          const stepLabels = {
                            inicio: '🟡 Inicio',
                            nombre: '🟡 Nombre',
                            perfil: '🟠 Perfil',
                            edad: '🟠 Edad',
                            ingreso: '🟠 Ingreso',
                            capacidad: '🔵 Capacidad',
                            plazo: '🔵 Plazo',
                            diagnostico: '🟣 Diagnóstico',
                            telefono: '🟢 Teléfono',
                            completado: '✅ Completado'
                          };
                          return (
                          <tr key={lead.id} className="hover:bg-stone-50 transition-colors">
                            <td className="px-6 py-4">
                              <p className="font-medium text-stone-900">{lead.name}</p>
                            </td>
                            <td className="px-6 py-4">
                              <p className="text-sm text-stone-600">{lead.email || '-'}</p>
                              <p className="text-sm text-stone-400">{lead.phone || '-'}</p>
                            </td>
                            <td className="px-6 py-4">
                              <span className="px-3 py-1 rounded-full bg-[#E5D4CE]/50 text-[#0F2B5B] text-sm">
                                {lead.profile_type}
                              </span>
                            </td>
                            <td className="px-6 py-4">
                              <span className={`text-sm ${
                                lead.last_step === 'completado' ? 'text-green-600 font-medium' : 
                                lead.last_step === 'telefono' ? 'text-green-500' :
                                lead.last_step === 'diagnostico' ? 'text-purple-600' : 'text-stone-500'
                              }`}>
                                {stepLabels[lead.last_step] || lead.last_step || '🟡 Inicio'}
                              </span>
                              {lead.total_messages > 0 && (
                                <p className="text-xs text-stone-400">{lead.total_messages} msgs</p>
                              )}
                            </td>
                            <td className="px-6 py-4 text-stone-600">
                              {lead.monthly_income ? `$${lead.monthly_income.toLocaleString()}` : '-'}
                            </td>
                            <td className="px-6 py-4">
                              <div className="flex items-center gap-2">
                                {/* PDF Downloaded indicator */}
                                <div className={`flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium ${
                                  lead.pdf_downloaded 
                                    ? 'bg-blue-100 text-blue-700' 
                                    : 'bg-stone-100 text-stone-400'
                                }`} title={lead.pdf_downloaded ? `PDF descargado` : 'PDF no descargado'}>
                                  <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 fill-current">
                                    <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20M12,19L8,15H10.5V12H13.5V15H16L12,19Z" />
                                  </svg>
                                  PDF
                                </div>
                                {/* WhatsApp Clicked indicator */}
                                <div className={`flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium ${
                                  lead.whatsapp_clicked 
                                    ? 'bg-green-100 text-green-700' 
                                    : 'bg-stone-100 text-stone-400'
                                }`} title={lead.whatsapp_clicked ? `WhatsApp clickeado` : 'WhatsApp no clickeado'}>
                                  <MessageCircle className="w-3.5 h-3.5" />
                                  WA
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <select
                                value={lead.status}
                                onChange={(e) => handleStatusChange('lead', lead.id, e.target.value)}
                                className={`px-3 py-1 rounded-full text-sm border-0 cursor-pointer ${getStatusColor(lead.status)}`}
                              >
                                <option value="new">Nuevo</option>
                                <option value="contacted">Contactado</option>
                                <option value="qualified">Calificado (Calendly)</option>
                                <option value="qualified_pending">Calificado (WhatsApp)</option>
                                <option value="appointment_scheduled">Cita agendada</option>
                                <option value="converted">Convertido</option>
                                <option value="newsletter">Newsletter</option>
                              </select>
                            </td>
                            <td className="px-6 py-4 text-sm text-stone-500">
                              {format(new Date(lead.created_at), 'dd MMM yyyy HH:mm', { locale: es })}
                            </td>
                            <td className="px-6 py-4">
                              {lead.phone && (
                                <a
                                  href={`https://wa.me/52${lead.phone.replace(/\D/g, '')}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="flex items-center gap-1 px-3 py-1.5 bg-[#25D366] hover:bg-[#20bd5a] text-white rounded-lg text-sm font-medium transition-all"
                                >
                                  <Send size={14} />
                                  WhatsApp
                                </a>
                              )}
                            </td>
                          </tr>
                        );})
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Appointments Table */}
            {activeTab === 'appointments' && (
              <div className="bg-white rounded-xl border border-stone-100 shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-stone-50 border-b border-stone-100">
                      <tr>
                        <th className="text-left px-6 py-4 text-sm font-medium text-stone-600">Cliente</th>
                        <th className="text-left px-6 py-4 text-sm font-medium text-stone-600">Contacto</th>
                        <th className="text-left px-6 py-4 text-sm font-medium text-stone-600">Fecha/Hora</th>
                        <th className="text-left px-6 py-4 text-sm font-medium text-stone-600">Perfil</th>
                        <th className="text-left px-6 py-4 text-sm font-medium text-stone-600">Estado</th>
                        <th className="text-left px-6 py-4 text-sm font-medium text-stone-600">Notas</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-stone-100">
                      {appointments.length === 0 ? (
                        <tr>
                          <td colSpan={6} className="text-center py-12 text-stone-500">
                            No hay citas agendadas aún.
                          </td>
                        </tr>
                      ) : (
                        appointments.map(appt => (
                          <tr key={appt.id} className="hover:bg-stone-50 transition-colors">
                            <td className="px-6 py-4">
                              <p className="font-medium text-stone-900">{appt.lead_name}</p>
                            </td>
                            <td className="px-6 py-4">
                              <p className="text-sm text-stone-600">{appt.lead_email || '-'}</p>
                              <p className="text-sm text-stone-400">{appt.lead_phone || '-'}</p>
                            </td>
                            <td className="px-6 py-4">
                              <p className="font-medium text-stone-900">
                                {format(new Date(appt.scheduled_datetime), 'dd MMM yyyy', { locale: es })}
                              </p>
                              <p className="text-sm text-stone-500">
                                {format(new Date(appt.scheduled_datetime), 'HH:mm', { locale: es })} hrs
                              </p>
                            </td>
                            <td className="px-6 py-4">
                              <span className="px-3 py-1 rounded-full bg-[#E5D4CE]/50 text-[#0F2B5B] text-sm">
                                {appt.profile_type}
                              </span>
                            </td>
                            <td className="px-6 py-4">
                              <select
                                value={appt.status}
                                onChange={(e) => handleStatusChange('appointment', appt.id, e.target.value)}
                                className={`px-3 py-1 rounded-full text-sm border-0 cursor-pointer ${getStatusColor(appt.status)}`}
                              >
                                <option value="scheduled">Agendada</option>
                                <option value="completed">Completada</option>
                                <option value="cancelled">Cancelada</option>
                                <option value="no_show">No asistió</option>
                              </select>
                            </td>
                            <td className="px-6 py-4 text-sm text-stone-500 max-w-xs truncate">
                              {appt.notes || '-'}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Newsletter Table */}
            {activeTab === 'newsletter' && (
              <div className="bg-white rounded-xl border border-stone-100 shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-stone-50 border-b border-stone-100">
                      <tr>
                        <th className="text-left px-6 py-4 text-sm font-medium text-stone-600">Email</th>
                        <th className="text-left px-6 py-4 text-sm font-medium text-stone-600">Nombre</th>
                        <th className="text-left px-6 py-4 text-sm font-medium text-stone-600">Fuente</th>
                        <th className="text-left px-6 py-4 text-sm font-medium text-stone-600">Fecha</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-stone-100">
                      {subscribers.length === 0 ? (
                        <tr>
                          <td colSpan={4} className="text-center py-12 text-stone-500">
                            No hay suscriptores aún.
                          </td>
                        </tr>
                      ) : (
                        subscribers.map((sub, index) => (
                          <tr key={index} className="hover:bg-stone-50 transition-colors">
                            <td className="px-6 py-4 font-medium text-stone-900">{sub.email}</td>
                            <td className="px-6 py-4 text-stone-600">{sub.name || '-'}</td>
                            <td className="px-6 py-4">
                              <span className="px-3 py-1 rounded-full bg-stone-100 text-stone-600 text-sm">
                                {sub.source}
                              </span>
                            </td>
                            <td className="px-6 py-4 text-sm text-stone-500">
                              {format(new Date(sub.created_at), 'dd MMM yyyy', { locale: es })}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Admin;
